import {
  createContext, useContext, useEffect, useState,
  useMemo, type ReactNode,
} from 'react'
import { getGradeTier, type GradeTier } from '@/lib/grade-tiers'
import { useAuth } from '@/context/AuthContext'

// ── Grade-tier theme config ───────────────────────────────────────────────────

export const GRADE_TIER_THEMES: Record<GradeTier, {
  accentColor: string
  bgPattern:   string
  iconSet:     string
  fontScale:   string
}> = {
  'K-2':  { accentColor: '#F59E0B', bgPattern: 'dots',  iconSet: 'playful',  fontScale: 'lg'   },
  '3-5':  { accentColor: '#10B981', bgPattern: 'grid',  iconSet: 'standard', fontScale: 'base' },
  '6-8':  { accentColor: '#4F46E5', bgPattern: 'none',  iconSet: 'standard', fontScale: 'base' },
  '9-12': { accentColor: '#6366F1', bgPattern: 'none',  iconSet: 'academic', fontScale: 'base' },
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type ColorSchemePreference = 'system' | 'light' | 'dark'

const STORAGE_KEY = 'nudge-color-scheme'
const VALID_PREFS: ColorSchemePreference[] = ['system', 'light', 'dark']

interface ThemeContextValue {
  gradeTier:                GradeTier
  theme:                    typeof GRADE_TIER_THEMES[GradeTier]
  colorScheme:              'light' | 'dark'
  colorSchemePreference:    ColorSchemePreference
  setColorSchemePreference: (pref: ColorSchemePreference) => void
}

// ── Context ───────────────────────────────────────────────────────────────────

const ThemeContext = createContext<ThemeContextValue | null>(null)

// ── Provider ──────────────────────────────────────────────────────────────────

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth()

  // Rule 2 — derive from profile, never hardcode; default to 6-8 before profile loads
  const gradeTier: GradeTier = (profile?.grade_level != null)
    ? getGradeTier(profile.grade_level)
    : '6-8'

  // User's explicit preference (system / light / dark), persisted to localStorage
  const [colorSchemePreference, setColorSchemePreferenceState] = useState<ColorSchemePreference>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    return VALID_PREFS.includes(stored as ColorSchemePreference)
      ? (stored as ColorSchemePreference)
      : 'system'
  })

  // Current OS preference — kept in state so it can be updated when the OS changes
  const [systemColorScheme, setSystemColorScheme] = useState<'light' | 'dark'>(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  )

  // Sync with OS color scheme changes (only matters when preference === 'system')
  useEffect(() => {
    const mq      = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => setSystemColorScheme(e.matches ? 'dark' : 'light')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Derived — no extra state needed.
  // 'light' / 'dark' overrides the OS; 'system' defers to it.
  const colorScheme: 'light' | 'dark' =
    colorSchemePreference === 'light'  ? 'light' :
    colorSchemePreference === 'dark'   ? 'dark'  :
    systemColorScheme

  // Persist preference and update state in one call
  function setColorSchemePreference(pref: ColorSchemePreference) {
    setColorSchemePreferenceState(pref)
    localStorage.setItem(STORAGE_KEY, pref)
  }

  // Apply data attributes to <html> — CSS and Tailwind key off these
  useEffect(() => {
    document.documentElement.setAttribute('data-grade-tier',   gradeTier)
    document.documentElement.setAttribute('data-color-scheme', colorScheme)
  }, [gradeTier, colorScheme])

  // Keep the PWA theme-color meta tag in sync (status bar colour on Android)
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) meta.setAttribute('content', colorScheme === 'dark' ? '#1E293B' : '#4F46E5')
  }, [colorScheme])

  const value = useMemo<ThemeContextValue>(() => ({
    gradeTier,
    theme:                    GRADE_TIER_THEMES[gradeTier],
    colorScheme,
    colorSchemePreference,
    setColorSchemePreference,
  }), [gradeTier, colorScheme, colorSchemePreference])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error(
    '[Nudge] useTheme must be used inside <ThemeProvider>. ' +
    'Wrap your app root — see state-managementSKILL.md.'
  )
  return ctx
}
